/**
 * OrganizationQuickCreateDialog - Minimal dialog for quick company creation.
 *
 * Fields: Company query (name/website/domain → AI lookup), Name, Relationship, Organization Type.
 * Redirects to detail page after creation for full editing.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Check, Sparkles } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Button } from '../../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { CompanyLookupInput, DialogForm } from '../../../../shared/components/common';
import { useApiError } from '../../../../shared/hooks/useApiError';
import { useCreateOrganization } from '../hooks/useOrganizations';
import { useTiers } from '../hooks/useTiers';
import { organizationApi } from '../services/contactsApi';
import { resolveEnrichmentTierId, uploadEnrichmentLogo } from '../utils/enrichment';
import { navigateToEntity } from '../../../../shared/utils/entityUrls';
import type { Organization, OrganizationCreate, DomainEnrichmentResult } from '../types';
import classificationConfig from '../../../../shared-config/business_classification.config.json';

interface OrganizationQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Callback when company is created (before navigation) */
  onCreated?: (company: Organization) => void;
  /** If true, don't navigate to detail page after creation */
  skipNavigation?: boolean;
  /** Pre-select these company relationship types (e.g., ['customer'] or ['vendor']) */
  defaultRelationships?: string[];
  /** Pre-fill the lookup field (e.g., from picker search text) */
  defaultLookup?: string;
  /** Pre-fill the name field (e.g., from picker search text) */
  defaultName?: string;
}

interface QuickCreateFormData {
  name: string;
  organization_relationship: string[];
  company_type: string;
  business_models?: string[];
  industry_verticals?: string[];
}

export function OrganizationQuickCreateDialog({
  open,
  onClose,
  onCreated,
  skipNavigation = false,
  defaultRelationships = [],
  defaultLookup = '',
  defaultName = '',
}: OrganizationQuickCreateDialogProps) {
  const { t } = useTranslation(['contacts', 'common']);
  const navigate = useNavigate();
  const createMutation = useCreateOrganization();
  const { data: tiers } = useTiers();
  const { handleMutationError } = useApiError({ showToast: false });

  const [lookupValue, setLookupValue] = useState(defaultLookup);
  const [resolvedDomain, setResolvedDomain] = useState<string | null>(null);
  const enrichedFieldsRef = useRef<Partial<DomainEnrichmentResult>>({});

  // Progressive disclosure: start on the "Auto-fill from the web" choice card.
  // Reveal the manual fields once the user skips the lookup or a match enriches.
  // If we were seeded with a name (e.g. from picker search), skip straight to manual.
  const [mode, setMode] = useState<'choice' | 'manual'>(defaultName ? 'manual' : 'choice');
  const [hasMatch, setHasMatch] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onSubmit',
    defaultValues: {
      name: defaultName,
      organization_relationship: defaultRelationships,
      company_type: 'corporation',
    },
  });

  const nameValue = watch('name');
  const companyType = watch('company_type');

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      reset({
        name: defaultName,
        organization_relationship: defaultRelationships,
        company_type: 'corporation',
      });
      setLookupValue(defaultLookup);
      setResolvedDomain(null);
      enrichedFieldsRef.current = {};
      setMode(defaultName ? 'manual' : 'choice');
      setHasMatch(false);
    }
    prevOpenRef.current = open;
  }, [open, reset, defaultRelationships, defaultLookup, defaultName]);

  const handleEnrichment = useCallback(
    (result: DomainEnrichmentResult, domain: string) => {
      setResolvedDomain(domain);
      setHasMatch(true);
      setMode('manual');
      if (result.name && !nameValue) {
        setValue('name', result.name, { shouldValidate: true });
      }
      if (result.business_models?.length) {
        setValue('business_models', result.business_models);
      }
      if (result.industry_verticals?.length) {
        setValue('industry_verticals', result.industry_verticals);
      }
      enrichedFieldsRef.current = {
        industry: result.industry,
        employee_count: result.employee_count,
        description: result.description,
        website: result.website,
        linkedin_url: result.linkedin_url,
        tier_name: result.tier_name,
        country: result.country,
        city: result.city,
        logo_url: result.logo_url,
      };
    },
    [nameValue, setValue],
  );

  const preLookupCheck = useCallback(
    async (query: string): Promise<string | null> => {
      // Only check duplicates when the input is a clear domain-shaped string.
      const looksLikeDomain = /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(query.trim());
      if (!looksLikeDomain) return null;
      try {
        const result = await organizationApi.checkDomain(query.trim());
        if (!result.available) {
          return t('organizations.domainInUse', { companyName: result.company_name });
        }
      } catch {
        // Non-actionable: skip duplicate warning if the check fails.
      }
      return null;
    },
    [t],
  );

  const onSubmit = async (data: QuickCreateFormData) => {
    const enriched = enrichedFieldsRef.current;

    const tierId = resolveEnrichmentTierId(enriched.tier_name, tiers);
    const logoFileId = enriched.logo_url
      ? await uploadEnrichmentLogo(enriched.logo_url, data.name)
      : undefined;

    const createData: OrganizationCreate = {
      name: data.name,
      domain: resolvedDomain || undefined,
      organization_relationship: data.organization_relationship.length > 0 ? data.organization_relationship : undefined,
      company_type: data.company_type || undefined,
      business_models: data.business_models?.length ? data.business_models : undefined,
      industry_verticals: data.industry_verticals?.length ? data.industry_verticals : undefined,
      industry: enriched.industry || undefined,
      employee_count: enriched.employee_count || undefined,
      description: enriched.description || undefined,
      website: enriched.website || undefined,
      linkedin_url: enriched.linkedin_url || undefined,
      country: enriched.country || undefined,
      city: enriched.city || undefined,
      tier_id: tierId,
      logo_file_id: logoFileId,
    };

    try {
      const newCompany = await createMutation.mutateAsync(createData);
      onCreated?.(newCompany);
      onClose();

      if (!skipNavigation) {
        navigate(`/master-data/organizations/${newCompany.id}`);
      }
    } catch (error) {
      handleMutationError(error, setError);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('organizations.create')}
      onSubmit={mode === 'manual' ? handleSubmit(onSubmit) : undefined}
      submitLabel={t('common:app.create')}
      cancelLabel={t('common:app.cancel')}
      isLoading={isSubmitting}
      maxWidth="sm"
    >
      <div className="space-y-4">
        {(mode === 'choice' || hasMatch) && (
          <CompanyLookupInput
            framed
            value={lookupValue}
            onChange={setLookupValue}
            onEnrichment={handleEnrichment}
            preLookupCheck={preLookupCheck}
            onSkip={() => setMode('manual')}
            onSelectExisting={(organizationId) => {
              onClose();
              navigateToEntity(navigate, 'companies', organizationId);
            }}
            hint={t('organizations.autoFillSubtitle', 'Type a name, website, or domain and we\'ll find the {{organization}} for you.')}
          />
        )}

        {mode === 'manual' && !hasMatch && (
          <button
            type="button"
            onClick={() => setMode('choice')}
            className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border py-2 text-sm text-muted-foreground transition hover:border-primary hover:text-foreground"
          >
            <Sparkles className="h-4 w-4 text-primary" />
            {t('organizations.autoFillReopen', 'Auto-fill from the web')}
          </button>
        )}

        {mode === 'manual' && (
        <>
        <div className="space-y-2">
          <Label htmlFor="name">{t('organizations.name')} *</Label>
          <Input
            {...register('name', { required: t('common:validation.required') })}
            id="name"
            className={cn(errors.name && 'border-destructive')}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>{t('common:businessClassification.companyRelationship.label')}</Label>
          <div className="flex flex-wrap gap-1.5">
            {classificationConfig.relationship_types.map((type) => {
              const isSelected = watch('organization_relationship')?.includes(type.value);
              return (
                <Button
                  key={type.value}
                  type="button"
                  variant={isSelected ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    const current = watch('organization_relationship') || [];
                    if (current.includes(type.value)) {
                      setValue('organization_relationship', current.filter((v) => v !== type.value));
                    } else {
                      setValue('organization_relationship', [...current, type.value]);
                    }
                  }}
                  className="gap-1"
                >
                  {isSelected && <Check className="h-3 w-3" />}
                  {t(`common:businessClassification.companyRelationship.${type.value}`)}
                </Button>
              );
            })}
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t('common:businessClassification.companyLegalType.label')}</Label>
          <Select
            value={companyType}
            onValueChange={(value) => setValue('company_type', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('common:businessClassification.companyLegalType.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {classificationConfig.company_legal_types.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {t(`common:businessClassification.companyLegalType.${type.value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <p className="text-xs text-muted-foreground">
          {t('organizations.quickCreateHint')}
        </p>
        </>
        )}
      </div>
    </DialogForm>
  );
}
